import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/PatientReportPanel.css';

const REPORT_TYPES = [
  { value: 'return_to_work', label: 'Return to work' },
  { value: 'ability_to_travel', label: 'Ability to travel' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/ai`;
const DOCTORS_URL = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/doctors/active`;

async function readApiError(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || err.message || fallback;
}

/**
 * Side panel for drafting a clinical letter and downloading it as a hospital PDF.
 * The standing instruction is sent on every generate and refine call.
 */
const PatientReportPanel = ({
  visible,
  onClose,
  source,
  admissionId,
  patientId,
}) => {
  const { token } = useAuth();
  const [reportType, setReportType] = useState('general');
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [instruction, setInstruction] = useState('');
  const [reportText, setReportText] = useState('');
  const [refineText, setRefineText] = useState('');
  const [refinements, setRefinements] = useState([]);
  const [loading, setLoading] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);

  useEffect(() => {
    if (!visible || !token) return undefined;
    let cancelled = false;
    axios.get(DOCTORS_URL, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (!cancelled) setDoctors(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the doctor list.');
      });
    return () => {
      cancelled = true;
    };
  }, [visible, token]);

  if (!visible) return null;

  const requestBody = (extra) => ({
    source,
    admission_id: admissionId || undefined,
    patient_id: patientId || undefined,
    report_type: reportType,
    instruction,
    ...extra,
  });

  const generateReport = async ({ currentReport, nextRefinements, action }) => {
    if (reportType === 'other' && !instruction.trim()) {
      setError('Add an instruction describing the report before generating an Other letter.');
      return false;
    }

    setLoading(action);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE}/patient-report`,
        requestBody({
          current_report: currentReport,
          refinements: nextRefinements,
        }),
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000,
        }
      );
      setReportText(response.data?.data?.report || '');
      setRefinements(nextRefinements);
      setCached(!!response.data?.cached);
      return true;
    } catch (err) {
      setError(await readApiError(err, 'Failed to generate the report'));
      return false;
    } finally {
      setLoading(null);
    }
  };

  const handleGenerate = () => {
    setRefineText('');
    generateReport({ currentReport: '', nextRefinements: [], action: 'generate' });
  };

  const handleRefine = async () => {
    const nextInstruction = refineText.trim();
    if (!nextInstruction) {
      setError('Enter a refinement instruction.');
      return;
    }
    if (!reportText.trim()) {
      setError('Generate a report before refining it.');
      return;
    }
    const saved = await generateReport({
      currentReport: reportText,
      nextRefinements: [...refinements, nextInstruction],
      action: 'refine',
    });
    if (saved) setRefineText('');
  };

  const handleDownload = async () => {
    if (!reportText.trim()) {
      setError('Generate a report before downloading the PDF.');
      return;
    }
    if (!doctorId) {
      setError('Select the reporting doctor before downloading the PDF.');
      return;
    }
    setDownloading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE}/patient-report/pdf`,
        requestBody({ report_text: reportText, doctor_id: Number(doctorId) }),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 180000,
        }
      );
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'PatientReport.pdf';
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(await readApiError(err, 'Failed to download the PDF'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="patient-report-backdrop" onClick={onClose}>
      <div className="patient-report-panel" onClick={(event) => event.stopPropagation()}>
        <div className="patient-report-header">
          <div>
            <h3>Patient Report</h3>
            {cached && <span className="patient-report-cached">Cached</span>}
          </div>
          <button type="button" className="patient-report-close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        <div className="patient-report-body">
          <label className="patient-report-label">
            Report type
            <select
              value={reportType}
              onChange={(event) => setReportType(event.target.value)}
              disabled={loading || downloading}
            >
              {REPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>

          <label className="patient-report-label">
            Reporting Doctor
            <select
              value={doctorId}
              onChange={(event) => setDoctorId(event.target.value)}
              disabled={loading || downloading}
            >
              <option value="">Select a doctor</option>
              {doctors.map((doctor) => (
                <option key={doctor.doctor_id} value={doctor.doctor_id}>
                  {doctor.doctor_name}{doctor.doctor_speciality ? ` (${doctor.doctor_speciality})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="patient-report-label">
            Instruction
            <textarea
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              disabled={loading || downloading}
              rows={4}
              placeholder={reportType === 'other'
                ? 'Required. Describe the report this letter should be.'
                : 'Optional. Sent with every draft, for example the job, the employer, or travel dates.'}
            />
          </label>
          <p className="patient-report-hint">
            Generate starts a new letter. The instruction above is included every time you generate or refine.
          </p>

          <button
            type="button"
            className="patient-report-primary"
            onClick={handleGenerate}
            disabled={loading || downloading}
          >
            {loading === 'generate' ? 'Writing report...' : 'Generate'}
          </button>

          {error && <div className="patient-report-error">{error}</div>}

          <label className="patient-report-label">
            Report
            <textarea
              className="patient-report-letter"
              value={reportText}
              onChange={(event) => setReportText(event.target.value)}
              disabled={loading || downloading}
              rows={16}
              placeholder={'CHIEF COMPLAINT\n\nHISTORY OF PRESENT ILLNESS\n\nDIAGNOSIS\n\nRECOMMENDATION'}
            />
          </label>

          <label className="patient-report-label">
            Refine
            <textarea
              value={refineText}
              onChange={(event) => setRefineText(event.target.value)}
              disabled={loading || downloading}
              rows={3}
              placeholder="Ask for a shorter letter, a clearer fitness statement, or another change."
            />
          </label>

          <div className="patient-report-actions">
            <button
              type="button"
              className="patient-report-secondary"
              onClick={handleRefine}
              disabled={loading || downloading || !reportText.trim()}
            >
              {loading === 'refine' ? 'Refining...' : 'Refine'}
            </button>
            <button
              type="button"
              className="patient-report-primary"
              onClick={handleDownload}
              disabled={loading || downloading || !reportText.trim() || !doctorId}
            >
              {downloading ? 'Preparing PDF...' : 'Download PDF'}
            </button>
          </div>
          <p className="patient-report-hint">
            The PDF adds the hospital letterhead, patient details, closing, and footer around these four sections.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PatientReportPanel;
